/**
 * School admin → Books.
 *
 * A book is a PDF a school gives ONE classroom, read by that class's students
 * under Learn → Class books. The shelf already lived on each classroom card,
 * behind a "Books" toggle — which is where nobody looked for it: a principal
 * asked to "upload the books" scans the sidebar, and a class card reads as a
 * place to set teachers, not to stock a shelf. So this screen gives books a
 * door of their own, and is nothing but that door: it lists the school's
 * classes and renders the very same `ClassBooksPanel` for the one chosen. The
 * upload, the presign, the ownership check on the key and the audit row are
 * all that panel's and the server's — there is still one shelf per class, and
 * one way to stock it.
 *
 * The chosen class rides in `?class=` so a refresh, or a link from elsewhere in
 * the console, lands on the same shelf.
 */
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MAX_BOOK_BYTES } from '@cwy/config/contracts';
import type { ClassroomDTO } from '@cwy/config/contracts';
import { apiGet } from '@/lib/api';
import { Card, Field, Select, StudentPage, StudentPageHeader } from '@/components/ds';
import { QueryState } from '@/components/admin/QueryState';
import { ClassBooksPanel } from '@/components/admin/ClassroomPanels';

const MAX_BOOK_MB = Math.floor(MAX_BOOK_BYTES / 1024 / 1024);

export function SchoolBooksPage() {
  const [params, setParams] = useSearchParams();
  // Shares its cache with the Classrooms screen: the same list, the same key.
  const classrooms = useQuery({
    queryKey: ['tenant-admin', 'classrooms'],
    queryFn: () => apiGet<ClassroomDTO[]>('/tenant-admin/classrooms'),
  });

  const list = useMemo(() => classrooms.data ?? [], [classrooms.data]);
  // An id that is not one of this school's live classes (an archived one, a
  // stale link) falls back to the first class rather than an empty panel.
  const selected = list.find((row) => row.id === params.get('class')) ?? list[0] ?? null;

  return (
    <StudentPage>
      <StudentPageHeader
        title="Books"
        subtitle="Give each class its textbooks and reading material. Students open them under Learn → Class books."
      />

      <QueryState
        isPending={classrooms.isPending}
        error={classrooms.error}
        isEmpty={list.length === 0}
        empty={
          <p className="text-sm text-ink-soft">
            Books are given to a class, and this school has no classes yet.{' '}
            <Link to="../classrooms" relative="path" className="font-semibold">
              Create a class first
            </Link>
            .
          </p>
        }
      >
        <Card padding="14px 16px">
          <ol className="flex list-decimal flex-col gap-1 pl-5 text-sm text-ink">
            <li>Choose the class the book is for.</li>
            <li>
              Choose a PDF (up to {MAX_BOOK_MB} MB) and give it a title students will recognise.
            </li>
            <li>
              Press <strong>Add book</strong>. Every student enrolled in that class sees it at once.
            </li>
          </ol>
          <p className="mt-2 text-xs text-ink-soft">
            A book is for one class. To give the same book to two sections, add it to each.
          </p>
        </Card>

        {selected && (
          <Card padding="16px">
            <Field label="Class" className="max-w-sm">
              {({ id }) => (
                <Select
                  id={id}
                  value={selected.id}
                  onChange={(event) => setParams({ class: event.target.value }, { replace: true })}
                >
                  {list.map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.name} · {row.studentCount}{' '}
                      {row.studentCount === 1 ? 'student' : 'students'}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            {/* Keyed by class, so a half-chosen file never carries to another shelf. */}
            <ClassBooksPanel key={selected.id} classroom={selected} />
          </Card>
        )}
      </QueryState>
    </StudentPage>
  );
}
